import { AppError, type RestResponse } from "@artisancode/types";
import { Pencil, Send, Star, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ConnectionRow } from "@/components/shared/connection-row";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type {
  useRemoveResendConnection,
  useSetPrimaryResendConnection,
} from "@/hooks/use-resend-connections";

import type { ResendConnection } from "@artisancode/api-types";

function errorMessage(error: unknown, fallback: string) {
  if (error instanceof AppError && (error.httpCode ?? 0) < 500) {
    const backendMessage = (error.data as RestResponse | undefined)?.message;
    return backendMessage || error.message;
  }
  return fallback;
}

export function ResendConnectionRow({
  connection,
  setPrimary,
  remove,
  onEdit,
}: {
  connection: ResendConnection;
  setPrimary: ReturnType<typeof useSetPrimaryResendConnection>;
  remove: ReturnType<typeof useRemoveResendConnection>;
  onEdit: (connection: ResendConnection) => void;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function handleSetPrimary() {
    try {
      await setPrimary.mutateAsync(connection.id);
      toast.success("Koneksi utama diperbarui.");
    } catch (error) {
      toast.error(errorMessage(error, "Gagal menjadikan koneksi ini utama."));
    }
  }

  async function handleRemove() {
    try {
      await remove.mutateAsync(connection.id);
      toast.success("Koneksi dihapus.");
      setConfirmOpen(false);
    } catch (error) {
      toast.error(errorMessage(error, "Gagal menghapus koneksi."));
    }
  }

  return (
    <>
      <ConnectionRow
        icon={Send}
        title={connection.name}
        subtitle={connection.fromAddress}
        badge={
          connection.isPrimary && <Badge className="shrink-0">Utama</Badge>
        }
        actions={
          <>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Edit"
              onClick={() => onEdit(connection)}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            {!connection.isPrimary && (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Jadikan utama"
                onClick={handleSetPrimary}
                disabled={setPrimary.isPending}
              >
                <Star className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Hapus"
              onClick={() => setConfirmOpen(true)}
            >
              <Trash2 className="h-3.5 w-3.5 text-destructive" />
            </Button>
          </>
        }
      />
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Hapus koneksi Resend ini?"
        confirmLabel="Hapus"
        onConfirm={handleRemove}
        isConfirming={remove.isPending}
      />
    </>
  );
}
